import React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { SettingsLayout } from '../../components/Pleri/components/Settings/SettingsLayout';
import {
  usePleriRender,
  PleriRenderType,
} from '../../components/Pleri/context/PleriRenderContext';
import { PleriLogo } from '../../components/Pleri/components/PleriLogo';

import { useTranslation } from 'react-i18next';

// Visual preview components for each trigger type
const SidebarPreview = ({ isActive }: { isActive: boolean }) => (
  <div
    className={`relative h-24 w-full overflow-hidden rounded-md border transition-all ${
      isActive
        ? 'border-orange-400 bg-gray-50 dark:bg-gray-800'
        : 'border-gray-200 bg-gray-50/50 dark:border-gray-700 dark:bg-gray-800/50'
    }`}
  >
    <div className="flex h-full">
      {/* Main content area */}
      <div className="flex-1 bg-gradient-to-br from-gray-100 to-gray-50 px-2 py-1.5 dark:from-gray-800 dark:to-gray-900">
        <div className="mb-1 h-1.5 w-11 rounded bg-gray-300 dark:bg-gray-600" />
        <div className="mb-0.5 h-1 w-full rounded bg-gray-200 dark:bg-gray-700" />
        <div className="h-1 w-3/4 rounded bg-gray-200 dark:bg-gray-700" />
      </div>
      {/* Sidebar trigger preview */}
      <div
        className={`flex w-6 flex-col items-center pt-1.5 ${isActive ? 'bg-orange-100 dark:bg-gray-700' : 'bg-gray-200 dark:bg-gray-700'}`}
      >
        <div className="mt-0.5 flex items-center justify-center">
          <PleriLogo size={18} className="rounded-md shadow" />
        </div>
        <div className="flex-1" />
      </div>
    </div>
  </div>
);

const NavbarPreview = ({ isActive }: { isActive: boolean }) => {
  const { t } = useTranslation('pleri/pages');

  return (
    <div
      className={`relative h-32 w-full overflow-hidden rounded-md border transition-all ${
        isActive
          ? 'border-orange-400 bg-gray-50 dark:bg-gray-800'
          : 'border-gray-200 bg-gray-50/50 dark:border-gray-700 dark:bg-gray-800/50'
      }`}
    >
      {/* Navbar */}
      <div className="flex items-center justify-between border-b border-gray-300 bg-white px-2 py-1.5 dark:border-gray-600 dark:bg-gray-900">
        <div className="flex gap-2">
          <div className="h-1 w-7 rounded bg-gray-300 dark:bg-gray-600" />
          <div className="h-1 w-7 rounded bg-gray-300 dark:bg-gray-600" />
        </div>
        {/* PleriLogo in navbar */}
        <div className="flex flex-shrink-0 items-center gap-2 rounded-md text-xs">
          {t('askPleri')} <PleriLogo size={18} className="rounded-md shadow" />
        </div>
      </div>
      {/* Content */}
      <div className="bg-gradient-to-br from-gray-100 to-gray-50 px-2 py-1.5 dark:from-gray-800 dark:to-gray-900">
        <div className="mb-1 h-1.5 w-11 rounded bg-gray-300 dark:bg-gray-600" />
        <div className="mb-0.5 h-1 w-full rounded bg-gray-200 dark:bg-gray-700" />
        <div className="h-1 w-3/4 rounded bg-gray-200 dark:bg-gray-700" />
      </div>
    </div>
  );
};

export const Route = createFileRoute('/settings/preferences')({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation('pleri/pages');
  // Handle case where context might not be available
  let renderType: PleriRenderType = 'sidebar';
  let setRenderType: (type: PleriRenderType) => void = () => {};

  try {
    const context = usePleriRender();
    renderType = context.renderType;
    setRenderType = context.setRenderType;
  } catch {
    // Context not available, use defaults
  }

  const triggerTypes: Array<{
    type: PleriRenderType;
    label: string;
    description: string;
    preview: React.ComponentType<{ isActive: boolean }>;
  }> = [
    {
      type: 'sidebar',
      label: t('sidebarPlacement'),
      description: t('builtInPleriPlacementOnTheRight'),
      preview: SidebarPreview,
    },
    {
      type: 'navbar',
      label: t('navbarPlacement'),
      description: t('integratePlacementInYourNavigation'),
      preview: NavbarPreview,
    },
  ];

  const handleSelect = (
    e: React.MouseEvent<HTMLButtonElement>,
    type: PleriRenderType,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setRenderType(type);
  };

  return (
    <SettingsLayout>
      <div className="flex h-full w-full flex-col overflow-y-auto p-8">
        <div className="flex w-full flex-1 justify-center">
          <div className="w-full max-w-[800px] space-y-8">
            {/* Pleri Placement Section */}
            <div>
              <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">
                {t('pleriPlacement')}
              </h3>
              <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                {t('chooseWhereYouWantPleriToAppear')}
              </p>
              <div className="grid grid-cols-2 gap-4">
                {triggerTypes.map((type) => {
                  const isSelected = type.type === renderType;
                  const Preview = type.preview;

                  return (
                    <button
                      key={type.type}
                      type="button"
                      onClick={(e) => handleSelect(e, type.type)}
                      className={`group relative w-full rounded-lg border bg-white p-4 text-left transition-all dark:bg-gray-900 ${
                        isSelected
                          ? 'border-orange-400 bg-orange-50/50 dark:bg-gray-800'
                          : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50/50 dark:border-gray-700'
                      }`}
                    >
                      <div className="mb-3 flex items-center justify-between">
                        <div>
                          <div
                            className={`text-sm font-medium ${
                              isSelected
                                ? 'text-orange-900 dark:text-gray-300'
                                : 'text-gray-900 dark:text-gray-100'
                            }`}
                          >
                            {type.label}
                          </div>
                          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            {type.description}
                          </div>
                        </div>
                        {isSelected && (
                          <div className="flex h-5 w-5 items-center justify-center rounded-full bg-orange-50 dark:bg-gray-800">
                            <svg
                              className="h-3 w-3 text-white"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        )}
                      </div>
                      <Preview isActive={isSelected} />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </SettingsLayout>
  );
}
